// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { Moves, Smsmessages } from '@gql/base-types';
import { useGetAllSmsMessagesQuery } from '../queries/getAllSmsMessages.query.generated';
import { useGetConsumerPickupMovesQuery } from '../queries/getConsumerPickupMoves.query.generated';
import {
  updateMoveReadStatuses,
  cleanupOldStatuses,
  getAllReadStatuses,
} from '@features/moves/services/smsReadStatusTracker';
import { useUser, useRooftop, useRooftops } from '@store';
import { useValetSelfManaged } from '@hopdrive/feature-portal-products';
import dayjs from 'dayjs';

// TYPES ---------------------------------------------------------------- //

export interface UnreadMove {
  moveId: number;
  move?: Moves;
  lastMessage?: Smsmessages;
  messageCount: number;
  hasUnread: boolean;
  timeSinceLastMessage?: string;
}

interface UseUnreadMessages {
  unreadMoves: UnreadMove[];
  allMoves: UnreadMove[]; // New: all consumer pickup moves with SMS
  totalUnreadCount: number;
  loading: boolean;
  error: boolean;
  refetch: () => void;
}

export interface UnreadMessagesOptions {
  /** Scope the unread scan to this date window (ISO timestamps) instead of the default last-30-days. Both must be provided to take effect. */
  start?: string;
  end?: string;
}

// HELPERS ---------------------------------------------------------------- //

/**
 * Shallow equality over the fields consumers of UnreadMove actually read. Used to keep
 * `unreadMoves`/`allMoves` referentially stable when a recompute produces the same result,
 * so downstream effects depending on those arrays don't re-fire for no reason.
 * Exported for tests.
 */
export const sameMoves = (a: UnreadMove[], b: UnreadMove[]): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (
      x.moveId !== y.moveId ||
      x.hasUnread !== y.hasUnread ||
      x.messageCount !== y.messageCount ||
      x.lastMessage?.id !== y.lastMessage?.id ||
      x.timeSinceLastMessage !== y.timeSinceLastMessage
    ) {
      return false;
    }
  }
  return true;
};

// HOOK ---------------------------------------------------------------- //

export const useUnreadMessages = (options?: UnreadMessagesOptions): UseUnreadMessages => {
  const user = useUser();
  const rooftop = useRooftop();

  // The working rooftop's Valet is self-managed-capable → SMS features are available.
  const isSmsEnabled = useValetSelfManaged();
  const linkedRooftops = useRooftops();

  // The rooftops to watch: a multi-rooftop user's linked rooftops (the header selector's list, from
  // usertocustomers, never the token's allowed customers), else the current rooftop or the user's
  // own customer. An admin's list is every rooftop, so an admin watches the current one alone.
  const allowedCustomerIds = React.useMemo(() => {
    if (!isSmsEnabled) {
      return [];
    }
    const isAdmin = user?.claims?.['x-hasura-default-role'] === 'admin';
    if (!isAdmin && linkedRooftops?.length) {
      return linkedRooftops.map(r => Number(r.id));
    }
    const singleCustomerId = rooftop?.id || user?.profile?.customer_id;
    return singleCustomerId ? [singleCustomerId] : [];
  }, [user, rooftop, linkedRooftops, isSmsEnabled]);
  
  // State for tracking moves
  const [unreadMoves, setUnreadMoves] = React.useState<UnreadMove[]>([]);
  const [allMoves, setAllMoves] = React.useState<UnreadMove[]>([]);
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);
  

  // Clean up old statuses on mount
  React.useEffect(() => {
    cleanupOldStatuses();
  }, []);

  // Scan window for consumer_pickup moves. Defaults to the last 30 days so global
  // consumers (e.g. the header dropdown) catch older unread messages; the moves
  // dashboard passes its selected date range so the tile count matches the rows the
  // date-filtered table can actually show.
  const scopedToRange = Boolean(options?.start && options?.end);
  const dashboardStart = scopedToRange ? options!.start! : dayjs().subtract(30, 'days').startOf('day').format();
  const dashboardEnd = scopedToRange ? options!.end! : dayjs().add(1, 'day').endOf('day').format();
  
  const { 
    data: dashboardData, 
    loading: dashboardLoading,
    error: dashboardError 
  } = useGetConsumerPickupMovesQuery({
    variables: {
      start: dashboardStart,
      end: dashboardEnd,
      customerIds: allowedCustomerIds,
    },
    skip: !allowedCustomerIds.length || !isSmsEnabled,
    // Use cache-and-network to ensure we get updates
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-first', // After initial load, use cache
  });

  // Get moves with consumer_pickup from dashboard
  const consumerPickupMoveIds = React.useMemo(() => {
    if (!dashboardData?.moves) {
      return [];
    }
    
    const moveIds = dashboardData.moves
      .filter(move => move?.id && !move?.cancel_status) // Exclude cancelled moves
      .map(move => move.id as number);
    
    return moveIds;
  }, [dashboardData]);

  // Include moves from localStorage that have unread messages
  const [localStorageUnreadIds, setLocalStorageUnreadIds] = React.useState<number[]>([]);
  
  React.useEffect(() => {
    const initialStatuses = getAllReadStatuses();
    const unreadIds = Object.entries(initialStatuses)
      .filter(([_, status]) => status.hasUnread)
      .map(([moveId]) => parseInt(moveId, 10));
    setLocalStorageUnreadIds(unreadIds);
  }, []);

  // Batch move IDs for efficient querying (max 100 at a time)
  const batchedMoveIds = React.useMemo(() => {
    // Combine consumer pickup moves with any unread moves from localStorage. When scoped
    // to an explicit date range, the localStorage IDs are excluded — they can point at
    // moves outside the range, and the scoped count must only include in-range moves.
    const allMoveIds = scopedToRange
      ? [...new Set(consumerPickupMoveIds)]
      : [...new Set([...consumerPickupMoveIds, ...localStorageUnreadIds])];
    // Take first 100 moves for initial query
    return allMoveIds.slice(0, 100);
  }, [consumerPickupMoveIds, localStorageUnreadIds, scopedToRange]);

  // Fetch messages for consumer pickup moves
  const {
    data: messagesData,
    loading: messagesLoading,
    error: messagesError,
    refetch
  } = useGetAllSmsMessagesQuery({
    variables: {
      moveIds: batchedMoveIds,
    },
    skip: batchedMoveIds.length === 0 || !isSmsEnabled,
    // Use cache-and-network to ensure we get updates
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-first', // After initial load, use cache
  });

  // Process messages and localStorage to build move arrays
  React.useEffect(() => {
    // Skip processing if SMS not enabled
    if (!isSmsEnabled || !messagesData?.moves) {
      setUnreadMoves([]);
      setAllMoves([]);
      return;
    }

    // Group messages by move_id
    const messagesByMove = (messagesData.smsmessages || []).reduce((acc, message) => {
      const moveId = message.move_id?.toString();
      if (!moveId) return acc;
      
      if (!acc[moveId]) {
        acc[moveId] = [];
      }
      acc[moveId].push(message as Smsmessages);
      return acc;
    }, {} as Record<string, Smsmessages[]>);


    const unread: UnreadMove[] = [];
    const all: UnreadMove[] = [];

    // Persist every move's read status in ONE read and at most ONE write. Calling
    // updateMoveReadStatus per move re-parsed the entire status map twice per move
    // (getMoveReadStatus, then getAllReadStatuses) and re-serialised it to save —
    // with up to 100 moves here that is hundreds of synchronous, disk-backed
    // localStorage operations per pass, long enough to stop click events dispatching
    // while the page still paints and scrolls.
    const statuses = updateMoveReadStatuses(
      messagesData.moves
        .filter(move => move?.id && (messagesByMove[move.id.toString()]?.length ?? 0) > 0)
        .map(move => ({ moveId: move.id as number, messages: messagesByMove[move.id!.toString()] }))
    );

    // Process all moves that we have data for
    messagesData.moves.forEach(move => {
      if (!move?.id) return;
      
      const moveIdStr = move.id.toString();
      const messages = messagesByMove[moveIdStr] || [];

      // Read the status back out of the map computed above, rather than hitting
      // localStorage again for each move.
      const currentStatus = statuses[moveIdStr] || null;
      const hasUnread = currentStatus?.hasUnread || false;
      
      // Find the last message
      const lastMessage = messages.length > 0
        ? messages.sort((a, b) => new Date(b.createdat || 0).getTime() - new Date(a.createdat || 0).getTime())[0]
        : undefined;
      
      // Calculate time since last message
      const lastMessageTime = lastMessage?.createdat || currentStatus?.lastMessageTime;
      const timeSinceLastMessage = lastMessageTime
        ? dayjs().diff(dayjs(lastMessageTime), 'minute') < 60
          ? `${dayjs().diff(dayjs(lastMessageTime), 'minute')} min ago`
          : dayjs().diff(dayjs(lastMessageTime), 'hour') < 24
          ? `${dayjs().diff(dayjs(lastMessageTime), 'hour')} hr ago`
          : `${dayjs().diff(dayjs(lastMessageTime), 'day')} days ago`
        : 'Unknown';

      const moveData: UnreadMove = {
        moveId: move.id,
        move: move as Moves,
        lastMessage,
        messageCount: messages.length || currentStatus?.messageCount || 0,
        hasUnread,
        timeSinceLastMessage,
      };

      // Add to all moves
      all.push(moveData);

      // Add to unread if it has unread status
      if (hasUnread) {
        unread.push(moveData);
      }
    });

    // Sort both arrays by most recent message first
    const sortByRecentMessage = (a: UnreadMove, b: UnreadMove) => {
      const dateA = a.lastMessage?.createdat ? new Date(a.lastMessage.createdat).getTime() : 0;
      const dateB = b.lastMessage?.createdat ? new Date(b.lastMessage.createdat).getTime() : 0;
      return dateB - dateA;
    };

    unread.sort(sortByRecentMessage);
    all.sort(sortByRecentMessage);

    // Only publish new state when the derived result actually differs. Consumers depend on
    // the arrays' identity (effects list `unreadMoves` as a dependency and set state from it),
    // and both queries set notifyOnNetworkStatusChange, so a fresh identity on a no-op run
    // re-renders, re-runs this effect via messagesLoading/dashboardLoading, and loops until
    // React reports "Maximum update depth exceeded". Comparing by the fields consumers
    // actually read keeps the reference stable across no-op runs and still publishes when the
    // data really moves.
    setUnreadMoves(prev => (sameMoves(prev, unread) ? prev : unread));
    setAllMoves(prev => (sameMoves(prev, all) ? prev : all));
    // refreshTrigger is not read by this effect — it exists solely to force a re-run when a
    // read-status event fires (localStorage 'storage' or 'sms-read-status-changed'). Those
    // events mean the derived hasUnread values may have changed WITHOUT messagesData
    // changing, so the recompute is required; the sameMoves guards above then publish only
    // if the result actually differs. Removing this dep would make mark-as-read/unread
    // stop updating the dropdown; removing the guards would let each bump publish fresh
    // identities into consumers' effect deps.
  }, [messagesData, dashboardLoading, messagesLoading, refreshTrigger, isSmsEnabled]);

  // Listen for localStorage changes (for cross-tab updates)
  React.useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'sms-read-status') {
        setRefreshTrigger(prev => prev + 1);
      }
    };

    // Listen for custom event for instant updates within same tab
    const handleReadStatusChange = () => {
      setRefreshTrigger(prev => prev + 1);
    };
    
    // Listen for global SMS updates from subscription
    const handleSmsUpdate = (event: CustomEvent) => {
      const { affectedMoveIds, hasNewMessages } = event.detail || {};
      
      // Only refetch if there are actually new messages and they affect our moves
      if (hasNewMessages && affectedMoveIds?.length > 0) {
        // Check if any of the affected moves are in our current list
        const hasRelevantUpdate = affectedMoveIds.some((moveId: number) => 
          batchedMoveIds.includes(moveId)
        );
        
        if (hasRelevantUpdate) {
          refetch();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('sms-read-status-changed', handleReadStatusChange as EventListener);
    window.addEventListener('sms-messages-updated', handleSmsUpdate as EventListener);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sms-read-status-changed', handleReadStatusChange as EventListener);
      window.removeEventListener('sms-messages-updated', handleSmsUpdate as EventListener);
    };
  }, [refetch, batchedMoveIds]);

  return React.useMemo(() => ({
    unreadMoves: isSmsEnabled ? unreadMoves : [],
    allMoves: isSmsEnabled ? allMoves : [],
    totalUnreadCount: isSmsEnabled ? unreadMoves.length : 0,
    loading: isSmsEnabled ? (dashboardLoading || messagesLoading) : false,
    error: isSmsEnabled ? (!!dashboardError || !!messagesError) : false,
    refetch: isSmsEnabled ? refetch : () => {},
  }), [unreadMoves, allMoves, dashboardLoading, messagesLoading, dashboardError, messagesError, refetch, isSmsEnabled]);
};

// EXPORT ---------------------------------------------------------------- //

export default useUnreadMessages;